import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  Send,
  Heart,
  Reply,
  MoreVertical,
  Edit2,
  Trash2,
  Flag,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { formatTimeAgo } from "@/lib/utils";
import announcementService from "@/services/announcement.service";

const roleColors = {
  admin: "bg-purple-100 text-purple-800 border-purple-200",
  rh: "bg-blue-100 text-blue-800 border-blue-200",
  manager: "bg-orange-100 text-orange-800 border-orange-200",
  employe: "bg-gray-100 text-gray-800 border-gray-200",
  stagiaire: "bg-green-100 text-green-800 border-green-200",
};

const roleLabels = {
  admin: "Admin",
  rh: "RH",
  manager: "Manager",
  employe: "Employé",
  stagiaire: "Stagiaire",
};

const CommentItem = ({
  comment,
  currentUser,
  onEdit,
  onDelete,
  canModerate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.commentaire);

  const isOwner = currentUser?.id === comment.employe?.id;
  const canEdit = isOwner;
  const canDelete = isOwner || canModerate;

  const handleSaveEdit = async () => {
    if (editText.trim() !== comment.commentaire) {
      await onEdit(comment.id, editText.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditText(comment.commentaire);
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="group flex space-x-3 p-3 rounded-lg hover:bg-gray-50/50 transition-colors"
    >
      {/* Avatar */}
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarImage src={comment.employe?.photo_profil} />
        <AvatarFallback className="text-xs">
          {comment.employe?.prenom?.[0]}
          {comment.employe?.nom?.[0]}
        </AvatarFallback>
      </Avatar>

      {/* Comment Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-sm text-gray-900">
              {comment.employe?.prenom} {comment.employe?.nom}
            </span>
            <Badge
              variant="outline"
              className={`text-xs ${
                roleColors[comment.employe?.role] || roleColors.employe
              }`}
            >
              {roleLabels[comment.employe?.role] || "Employé"}
            </Badge>
            <span className="text-xs text-gray-500">
              {formatTimeAgo(comment.date_commentaire)}
            </span>
          </div>

          {/* Actions Menu */}
          {(canEdit || canDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem onClick={() => setIsEditing(true)}>
                    <Edit2 className="h-3 w-3 mr-2" />
                    Modifier
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <>
                    {canEdit && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onClick={() => onDelete(comment.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-3 w-3 mr-2" />
                      Supprimer
                    </DropdownMenuItem>
                  </>
                )}
                {!isOwner && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-orange-600">
                      <Flag className="h-3 w-3 mr-2" />
                      Signaler
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Comment Text */}
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="min-h-[60px] text-sm"
              placeholder="Modifiez votre commentaire..."
            />
            <div className="flex justify-end space-x-2">
              <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={!editText.trim()}
              >
                Sauvegarder
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {comment.commentaire}
          </p>
        )}

        {/* Comment Actions */}
        <div className="flex items-center space-x-4 mt-2">
          <button className="flex items-center space-x-1 text-xs text-gray-500 hover:text-red-500 transition-colors">
            <Heart className="h-3 w-3" />
            <span>J'aime</span>
          </button>

          <button className="flex items-center space-x-1 text-xs text-gray-500 hover:text-blue-500 transition-colors">
            <Reply className="h-3 w-3" />
            <span>Répondre</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const EnhancedCommentSection = ({
  announcementId,
  initialCommentCount = 0,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({});

  // Permissions
  const canModerate = ["admin", "rh", "manager"].includes(user?.role);

  // Load comments when expanded
  const fetchComments = async (page = 1) => {
    if (!announcementId) return;

    try {
      setLoading(true);
      const response = await announcementService.getComments(announcementId, {
        page,
        limit: 10,
      });

      if (page === 1) {
        setComments(response.data.data || []);
      } else {
        setComments((prev) => [...prev, ...(response.data.data || [])]);
      }

      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error("Erreur lors du chargement des commentaires:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les commentaires",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const response = await announcementService.addComment(
        announcementId,
        newComment.trim()
      );

      // Add the new comment to the list
      const newCommentData = {
        id: response.data.data.id,
        commentaire: newComment.trim(),
        date_commentaire: new Date().toISOString(),
        employe: {
          id: user.id,
          prenom: user.prenom,
          nom: user.nom,
          role: user.role,
          photo_profil: user.photo_profil,
        },
      };

      setComments((prev) => [newCommentData, ...prev]);
      setNewComment("");

      toast({
        title: "Succès",
        description: "Commentaire ajouté avec succès",
      });
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter le commentaire",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, newText) => {
    toast({
      title: "Info",
      description: "Fonctionnalité d'édition en cours de développement",
    });
  };

  const handleDeleteComment = async (commentId) => {
    if (
      !window.confirm("Êtes-vous sûr de vouloir supprimer ce commentaire ?")
    ) {
      return;
    }

    toast({
      title: "Info",
      description: "Fonctionnalité de suppression en cours de développement",
    });
  };

  const handleToggleExpand = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    if (newExpanded && comments.length === 0) {
      fetchComments();
    }
  };

  return (
    <div className="border-t border-gray-100 bg-gray-50/30">
      {/* Comment Toggle Button */}
      <div className="px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggleExpand}
          className="flex items-center justify-between w-full text-gray-600 hover:text-gray-900 hover:bg-gray-100"
        >
          <div className="flex items-center space-x-2">
            <MessageCircle className="h-4 w-4" />
            <span>
              {initialCommentCount > 0
                ? `${initialCommentCount} commentaire${
                    initialCommentCount > 1 ? "s" : ""
                  }`
                : "Ajouter un commentaire"}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden bg-white/50 backdrop-blur-sm"
          >
            {/* New Comment Form */}
            <div className="px-4 pb-4">
              <div className="flex space-x-3">
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={user?.photo_profil} />
                  <AvatarFallback className="text-xs">
                    {user?.prenom?.[0]}
                    {user?.nom?.[0]}
                  </AvatarFallback>
                </Avatar>
                <form onSubmit={handleSubmitComment} className="flex-1">
                  <div className="space-y-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Ajoutez un commentaire professionnel..."
                      className="min-h-[80px] text-sm resize-none border-gray-200 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      disabled={submitting}
                    />
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        size="sm"
                        disabled={!newComment.trim() || submitting}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {submitting ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <>
                            <Send className="h-3 w-3 mr-1" />
                            Publier
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <Separator />

            {/* Comments List */}
            {comments.length > 0 && (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                <AnimatePresence>
                  {comments.map((comment) => (
                    <CommentItem
                      key={comment.id}
                      comment={comment}
                      currentUser={user}
                      onEdit={handleEditComment}
                      onDelete={handleDeleteComment}
                      canModerate={canModerate}
                    />
                  ))}
                </AnimatePresence>

                {/* Load More Comments */}
                {pagination.hasNextPage && (
                  <div className="px-4 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchComments(pagination.currentPage + 1)}
                      disabled={loading}
                      className="w-full text-gray-600 hover:text-gray-900"
                    >
                      {loading
                        ? "Chargement..."
                        : "Afficher plus de commentaires"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {comments.length === 0 && !loading && (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>Aucun commentaire pour le moment.</p>
                <p className="text-xs mt-1">
                  Soyez le premier à partager votre avis !
                </p>
              </div>
            )}

            {/* Loading State */}
            {loading && comments.length === 0 && (
              <div className="px-4 py-8 text-center">
                <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">
                  Chargement des commentaires...
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EnhancedCommentSection;
